import ViewerStatus from '@/components/pages/create-webinar/live/status/ViewerStatus';
import TitleLayout from '@/components/pages/create-webinar/TitleLayout';
import SurveysForm from '@/components/pages/create-webinar/live/survey/SurveysForm';
import QuizsForm from '@/components/pages/create-webinar/live/quiz/QuizsForm';
import BanWordsForm from '@/components/pages/create-webinar/live/BanWordsForm';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import Loader from '@/components/ui/Loader';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import type { InteractionGroupHandle } from '@/components/pages/create-webinar/live/interactionBatch';

interface InteractionSectionProps {
  currentWebinarId?: number | null;
  isEdit?: boolean;
}

export default function InteractionSection({ currentWebinarId, isEdit }: InteractionSectionProps) {
  const { t } = useTranslation('createWebinar');
  const { webinarId: webinarIdParam } = useParams<{ webinarId?: string }>();
  const webinarIdString = webinarIdParam || currentWebinarId;
  const webinarId =
    typeof webinarIdString === 'number' ? webinarIdString : parseInt(webinarIdString ?? '0', 10);
  const { currentWorkspace } = useAppSelector((state: RootState) => state.workspace);

  // Sauvegarde globale des sondages + quizz
  const surveysRef = useRef<InteractionGroupHandle>(null);
  const quizzesRef = useRef<InteractionGroupHandle>(null);
  const [surveysDirty, setSurveysDirty] = useState(false);
  const [quizzesDirty, setQuizzesDirty] = useState(false);
  const [savingSurveys, setSavingSurveys] = useState(false);
  const [savingQuizzes, setSavingQuizzes] = useState(false);

  // Enchaînement après sauvegarde : seuil → Sondages → Quiz → Mots à bannir. Ce dernier bloc
  // enregistre au fil de l'eau, la page n'a donc pas d'étape finale : on en sort par le menu.
  const [openSurveys, setOpenSurveys] = useState(false);
  const [openQuizzes, setOpenQuizzes] = useState(false);
  const [openBanWords, setOpenBanWords] = useState(false);

  // Toast d'échec ciblé : on nomme l'élément fautif si on connaît son titre,
  // sinon on retombe sur le message générique.
  const reportFailure = (
    result: { reason: 'validation' | 'error'; label?: string },
    kind: 'survey' | 'quiz',
  ) => {
    if (result.label) {
      const key =
        kind === 'survey'
          ? result.reason === 'validation'
            ? 'interactionSection.saveAllSurveyValidationError'
            : 'interactionSection.saveAllSurveyError'
          : result.reason === 'validation'
            ? 'interactionSection.saveAllQuizValidationError'
            : 'interactionSection.saveAllQuizError';
      toast.error(t(key, { name: result.label }));
      return;
    }
    toast.error(
      result.reason === 'validation'
        ? t('interactionSection.saveAllValidationError')
        : t('interactionSection.saveAllError'),
    );
  };

  const handleSaveSurveys = async () => {
    setSavingSurveys(true);
    try {
      const result = await surveysRef.current?.saveAll();
      if (result && !result.ok) {
        reportFailure(result, 'survey');
        return;
      }
      if (result?.ok) {
        if (result.saved > 0) {
          toast.success(t('interactionSection.saveAllSurveysSuccess'));
        }
        setOpenQuizzes(true);
      }
    } finally {
      setSavingSurveys(false);
    }
  };

  const handleSaveQuizzes = async () => {
    setSavingQuizzes(true);
    try {
      const result = await quizzesRef.current?.saveAll();
      if (result && !result.ok) {
        reportFailure(result, 'quiz');
        return;
      }
      if (result?.ok) {
        if (result.saved > 0) {
          toast.success(t('interactionSection.saveAllQuizzesSuccess'));
        }
        setOpenBanWords(true);
      }
    } finally {
      setSavingQuizzes(false);
    }
  };

  if (!webinarId || !currentWorkspace) {
    return <div>{t('liveSection.fetchError')}</div>;
  }

  return (
    <TitleLayout
      title={t('interactionSection.title')}
      subtitle={t('liveSection.subtitle')}
      isEdit={isEdit}
    >
      <ViewerStatus
        webinarId={webinarId}
        workspaceId={currentWorkspace.id}
        isEdit={isEdit}
        onThresholdSaved={() => setOpenSurveys(true)}
      />
      <Separator className="bg-card" />
      <SurveysForm
        ref={surveysRef}
        webinarId={webinarId}
        workspaceId={currentWorkspace.id}
        isEdit={isEdit}
        batchMode
        onDirtyChange={setSurveysDirty}
        openAccordionTrigger={openSurveys}
        onAccordionOpened={() => setOpenSurveys(false)}
      />
      <Button
        type="button"
        onClick={handleSaveSurveys}
        className="w-full disabled:bg-gray-200 disabled:text-gray-500"
        disabled={savingSurveys || !surveysDirty}
      >
        {savingSurveys && <Loader />}
        {t('interactionSection.saveSurveys')}
      </Button>
      <Separator className="bg-card" />
      <QuizsForm
        ref={quizzesRef}
        webinarId={webinarId}
        workspaceId={currentWorkspace.id}
        isEdit={isEdit}
        batchMode
        onDirtyChange={setQuizzesDirty}
        openAccordionTrigger={openQuizzes}
        onAccordionOpened={() => setOpenQuizzes(false)}
      />
      <Button
        type="button"
        onClick={handleSaveQuizzes}
        className="w-full disabled:bg-gray-200 disabled:text-gray-500"
        disabled={savingQuizzes || !quizzesDirty}
      >
        {savingQuizzes && <Loader />}
        {t('interactionSection.saveQuizzes')}
      </Button>
      <Separator className="bg-card" />
      <BanWordsForm
        isEdit={isEdit}
        openAccordionTrigger={openBanWords}
        onAccordionOpened={() => setOpenBanWords(false)}
      />
    </TitleLayout>
  );
}
